/**
 * W3CSHACLResultsPanel.tsx
 *
 * Displays W3C SHACL validation results in a user-friendly format
 */

import React, { useState } from 'react';

interface W3CViolation {
  ruleId: string;
  severity: "Violation" | "Warning" | "Info";
  focusNode: string;
  resultPath: string;
  message: string;
  value?: string;
}

interface W3CSHACLResultsPanelProps {
  findings: W3CViolation[];
  conforms?: boolean;
}

export function W3CSHACLResultsPanel({ findings, conforms }: W3CSHACLResultsPanelProps) {
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  const toggleRule = (ruleId: string) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };

  // Group violations by rule ID
  const violationsByRule = findings.reduce((acc, v) => {
    if (!acc[v.ruleId]) acc[v.ruleId] = [];
    acc[v.ruleId].push(v);
    return acc;
  }, {} as Record<string, W3CViolation[]>);

  const ruleIds = Object.keys(violationsByRule).sort();

  if (findings.length === 0) {
    return (
      <div className="w3c-shacl-results success">
        <h3>W3C SHACL Validation: PASS</h3>
        <p>All FAIR rules conform to W3C SHACL standards.</p>
      </div>
    );
  }

  return (
    <div className="w3c-shacl-results">
      <h3>
        {conforms ? '' : ''} W3C SHACL Validation Results
      </h3>
      <p className="summary">
        Found {findings.length} validation {findings.length === 1 ? 'issue' : 'issues'} across {ruleIds.length} FAIR {ruleIds.length === 1 ? 'rule' : 'rules'}.
      </p>

      <div className="violations-by-rule">
        {ruleIds.map(ruleId => {
          const ruleViolations = violationsByRule[ruleId];
          const isExpanded = expandedRules.has(ruleId);
          const violationCount = ruleViolations.filter(v => v.severity === 'Violation').length;
          const warningCount = ruleViolations.filter(v => v.severity === 'Warning').length;

          return (
            <div key={ruleId} className="rule-group">
              <div
                className="rule-header"
                onClick={() => toggleRule(ruleId)}
                style={{ cursor: 'pointer' }}
              >
                <span className="rule-id">{ruleId}</span>
                <span className="rule-counts">
                  {violationCount > 0 && <span className="violation-count">{violationCount}</span>}
                  {warningCount > 0 && <span className="warning-count">{warningCount}</span>}
                </span>
                <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
              </div>

              {isExpanded && (
                <div className="rule-violations">
                  {ruleViolations.map((v, idx) => (
                    <div key={idx} className={`violation ${v.severity.toLowerCase()}`}>
                      <div className="violation-message">{v.message}</div>
                      <div className="violation-details">
                        <span><strong>Focus Node:</strong> {v.focusNode}</span>
                        <span><strong>Path:</strong> {v.resultPath}</span>
                        {v.value && <span><strong>Value:</strong> {v.value}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        .w3c-shacl-results {
          margin: 2rem 0;
          padding: 1.5rem;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: #f9fafb;
        }

        .w3c-shacl-results.success {
          border-color: #10b981;
          background: #ecfdf5;
        }

        .w3c-shacl-results h3 {
          margin: 0 0 0.5rem 0;
          color: #111827;
        }

        .w3c-shacl-results .summary {
          margin: 0 0 1rem 0;
          color: #6b7280;
        }

        .violations-by-rule {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .rule-group {
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
        }

        .rule-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 1rem;
          background: #f3f4f6;
          border-radius: 6px 6px 0 0;
        }

        .rule-header:hover {
          background: #e5e7eb;
        }

        .rule-id {
          font-weight: 600;
          color: #1f2937;
          font-family: 'Monaco', 'Courier New', monospace;
        }

        .rule-counts {
          display: flex;
          gap: 0.5rem;
          margin-left: auto;
        }

        .violation-count, .warning-count {
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }

        .violation-count {
          background: #fee2e2;
          color: #991b1b;
        }

        .warning-count {
          background: #fef3c7;
          color: #92400e;
        }

        .expand-icon {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .rule-violations {
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .violation {
          padding: 0.75rem;
          border-left: 4px solid #ef4444;
          background: #fef2f2;
          border-radius: 4px;
        }

        .violation.warning {
          border-left-color: #f59e0b;
          background: #fffbeb;
        }

        .violation.info {
          border-left-color: #3b82f6;
          background: #eff6ff;
        }

        .violation-message {
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .violation-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .violation-details strong {
          color: #374151;
        }
      `}</style>
    </div>
  );
}
